import {
  calculateDarcyFrictionFactor,
  getFlowRegime,
} from "./pipeFlow";

export interface PipePressureDropInputs {
  innerDiameter: number;
  pipeLength: number;
  roughness: number;
  liquidFlow: number;
  density: number;
  viscosity: number;
  normalAirFlow: number;
  absoluteInletPressure: number;
}

export interface PipePressureDropResult {
  liquidVelocity: number;
  liquidReynoldsNumber: number;
  liquidFlowRegime: "laminar" | "turbulent";
  liquidFrictionFactor: number;
  liquidPressureDrop: number;
  compressedAirPressureDrop: number;
}

const calculateCompressedAirPressureDrop = (
  normalAirFlow: number,
  pipeLength: number,
  innerDiameter: number,
  absoluteInletPressure: number
) => {
  if (normalAirFlow === 0) return 0;

  const normalAirFlowM3PerHour = normalAirFlow * 3600;
  const innerDiameterMm = innerDiameter * 1000;
  const inletPressureBar = absoluteInletPressure / 1e5;

  const pressureDropBar =
    (1.6e12 *
      Math.pow(normalAirFlowM3PerHour / 3600, 1.85) *
      pipeLength) /
    (Math.pow(innerDiameterMm, 5) * inletPressureBar * 100) /
    100;
  return pressureDropBar * 1e5;
};

export const calculatePipePressureDrop = (
  input: PipePressureDropInputs
): PipePressureDropResult | null => {
  if (
    Object.values(input).some((value) => !Number.isFinite(value)) ||
    input.innerDiameter <= 0 ||
    input.pipeLength <= 0 ||
    input.roughness < 0 ||
    input.liquidFlow < 0 ||
    input.density <= 0 ||
    input.viscosity <= 0 ||
    input.normalAirFlow < 0 ||
    (input.normalAirFlow > 0 && input.absoluteInletPressure <= 0)
  ) {
    return null;
  }

  const pipeArea =
    (Math.PI * input.innerDiameter * input.innerDiameter) / 4;
  const liquidVelocity = input.liquidFlow / pipeArea;
  const liquidReynoldsNumber =
    input.liquidFlow === 0
      ? 0
      : (input.density * liquidVelocity * input.innerDiameter) /
        input.viscosity;
  const liquidFrictionFactor = calculateDarcyFrictionFactor(
    liquidReynoldsNumber,
    input.roughness,
    input.innerDiameter
  );
  const liquidPressureDrop =
    liquidFrictionFactor *
    (input.pipeLength / input.innerDiameter) *
    (input.density * liquidVelocity * liquidVelocity) /
    2;
  const compressedAirPressureDrop =
    calculateCompressedAirPressureDrop(
      input.normalAirFlow,
      input.pipeLength,
      input.innerDiameter,
      input.absoluteInletPressure
    );

  return {
    liquidVelocity,
    liquidReynoldsNumber,
    liquidFlowRegime: getFlowRegime(liquidReynoldsNumber),
    liquidFrictionFactor,
    liquidPressureDrop,
    compressedAirPressureDrop,
  };
};
